import { createAsyncThunk, createSlice, PayloadAction, unwrapResult } from '@reduxjs/toolkit';
import { RootState } from './store';
import { Fact } from './AfrSlice';
import { Concept, ConceptRef, Label, emptyConcept } from './ConceptSlice';
import { Node, Edge } from './EntitiesSlice';
import http from '../utils/http';
import { Afr } from './AfrSlice';
import { ActiveTaxonomyVersionInfo, TaxonomyTypeEnum } from './TaxonomyVersionSlice';

export interface Network {
    name: string;
    roots: Node[];
}
export interface DimensionalAccountScope {
    filter: string;
    accountMappings: any;
}

export interface NetworkDetailsRequest {
    networkId: string;
    nodeId: string;
    taxonomyInfo: ActiveTaxonomyVersionInfo;
}

export interface NetworkState {
    selectedNetworkRoot: Node | null;
    scopeNetworks: Network[];
    definitionNetworks: Network[];
    tableNetworks: Network[],
    presentationNetworks: Network[];
    status: 'idle' | 'loading' | 'failed';
}


const initialState: NetworkState = {
    selectedNetworkRoot: null,
    scopeNetworks: [],
    definitionNetworks: [],
    tableNetworks: [],
    presentationNetworks: [],
    status: "idle"
}


export const emptyNode: Node = {
    incoming: [],
    outgoing: [],
    name: "",
    id: "",
    concept: emptyConcept
}

export const loadDefinitionNetworks = createAsyncThunk(
    "Taxonomy/GetNetworks",
    async (vInfo: ActiveTaxonomyVersionInfo): Promise<Network[]> => {
        try {
            var ttype = vInfo.type;
            var entryPoint = vInfo.year + "";
            var ntype = "definition";
            const result = await http<undefined, Network[]>({
                path: `/taxonomy/${ttype}/${entryPoint}/networks/${ntype}`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            return [];
        }
    });

export const loadScopeNetworks = createAsyncThunk(
    "Taxonomy/GetScopeNetworks",
    async (vInfo: ActiveTaxonomyVersionInfo): Promise<Network[]> => {
        try {
            var ttype = vInfo.type;
            var entryPoint = vInfo.year + "";
            var ntype = "dimensional-scope";
            const result = await http<undefined, Network[]>({
                path: `/taxonomy/${ttype}/${entryPoint}/networks/${ntype}`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            return [];
        }
    });

export const loadTableNetworks = createAsyncThunk(
    "Taxonomy/GetTableNetworks",
    async (vInfo: ActiveTaxonomyVersionInfo): Promise<Network[]> => {
        try {
            var ttype = vInfo.type;
            var entryPoint = vInfo.year + "";
            var ntype = "table";
            const result = await http<undefined, Network[]>({
                path: `/taxonomy/${ttype}/${entryPoint}/networks/${ntype}`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return [];
            }
        } catch (ex) {
            return [];
        }
    });

export const loadDefinitionNetworkRoot = createAsyncThunk(
    "Taxonomy/GetNetworkDetail",
    async (req : NetworkDetailsRequest): Promise<Node> => {
        try {
            var ttype = req.taxonomyInfo.type
            var entryPoint = req.taxonomyInfo.year + "";
            var ntype = "definition";
            const result = await http<undefined, Node>({
                path: `/taxonomy/${ttype}/${entryPoint}/networks/${ntype}/${req.networkId}/${req.nodeId}`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return emptyNode;
            }
        } catch (ex) {
            return emptyNode;
        }
    });

const addTerminalConcept = function(node: Node, result: any[]) {
    if (node.outgoing && node.outgoing.length > 0) {
        node.outgoing.map(o => addTerminalConcept(o.to, result));
    } else {
        if (node.concept) {
            result.push(node.concept);
        }
    }
}
export const getAccountFilterForFundGroup = function (fundGroup: string, scopeNetworks: Network[]): DimensionalAccountScope {
    var scopeMapping: DimensionalAccountScope = {
        filter: "",
        accountMappings: {}
    };
    if (fundGroup && scopeNetworks.length > 0) {
        var fmatch = fundGroup.split(" ")[0].toLowerCase();
        for (var n of scopeNetworks) {
            if (n.name.indexOf("fund") > 0) {
                n.roots[0].outgoing.map((e: any) => {
                    var r = e.to;
                    if (r && r.concept && r.concept.code) {
                        var terminals: any[] = getTerminalConcepts(r);
                        scopeMapping.accountMappings[r.concept.code] = terminals;
                        var bAllowed = false;
                        for (var tConcept of terminals) {
                            if (tConcept.name.toLowerCase().indexOf(fmatch) >= 0) {
                                bAllowed = true;
                            }
                        }
                        if (!bAllowed) {
                            scopeMapping.filter += r.concept.code + ";";
                        }
                    }
                });
            }
        }
    }
    return scopeMapping;
}
export const getTerminalConcepts = function (root: Node): any[] {
    var result: any[] = [];
    addTerminalConcept(root, result);
    return result;
}

export const networkSlice = createSlice({
    name: 'Network',
    initialState,
    reducers: {
        resetNetworkState: (state: any) => {
            return initialState
        }
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(loadDefinitionNetworks.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadDefinitionNetworks.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.definitionNetworks = action.payload;
            })
            .addCase(loadDefinitionNetworkRoot.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadDefinitionNetworkRoot.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.selectedNetworkRoot = action.payload;
            })
            .addCase(loadTableNetworks.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadTableNetworks.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.tableNetworks = action.payload;
            })
            .addCase(loadScopeNetworks.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(loadScopeNetworks.fulfilled, (state: any, action: any) => {
                state.status = "idle";
                state.scopeNetworks = action.payload;
            })
    },
});

export const { resetNetworkState } = networkSlice.actions;

export const selectDefinitionNetworks = (state: RootState) => state.network ? state.network.definitionNetworks : [];

export const selectScopeNetworks = (state: RootState) => state.network ? state.network.scopeNetworks : [];

export const selectTableNetworks = (state: RootState) => state.network ? state.network.tableNetworks : [];

export const selectDefinitionNetworkRoot = (state: RootState) => state.network ? state.network.selectedNetworkRoot : null;

export default networkSlice.reducer;